import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';
import Container from '../GeneralComponents/Container';
import { SearchHero, ResultsGrid, EmptyState } from './components';
import { getPublicOrganizations, getPublicDealers } from '../../redux/actions/apiActions';
import { statesAndCities } from '../../utils/indianStatesCities';

const SearchSection = () => {
  const theme = useTheme();

  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchType, setSearchType] = useState('organizations');
  const [results, setResults] = useState({ organizations: [], dealers: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const limit = 25;

  useEffect(() => {
    const options = statesAndCities.map((item) => ({
      label: item.state,
      value: item.state,
    }));
    setStateOptions(options);
  }, []);

  useEffect(() => {
    if (selectedState) {
      const stateData = statesAndCities.find(
        (s) => s.state.toLowerCase() === selectedState.value.toLowerCase()
      );
      if (stateData) {
        const sortedCities = [...stateData.cities].sort((a, b) => a.localeCompare(b));
        setCityOptions(sortedCities.map((city) => ({ label: city, value: city })));
      }
    } else {
      setCityOptions([]);
      setSelectedCity(null);
    }
  }, [selectedState]);

  const handleStateChange = (option) => {
    setSelectedState(option);
    setSelectedCity(null);
    setHasSearched(false);
  };

  const handleCityChange = (option) => {
    setSelectedCity(option);
  };

  const handleTypeChange = (type) => {
    setSearchType(type);
  };

  const handleClearFilters = () => {
    setSelectedState(null);
    setSelectedCity(null);
    setSearchType('organizations');
    setResults({ organizations: [], dealers: [] });
    setTotal(0);
    setHasSearched(false);
    setPage(1);
    setHasMore(false);
  };

  const handleSearch = async (isLoadMore = false) => {
    if (!selectedState) {
      toast.warning('Please select a state to search');
      return;
    }

    const currentPage = isLoadMore ? page + 1 : 1;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
      });

      if (selectedState?.value) queryParams.append('state', selectedState.value);
      if (selectedCity?.value) queryParams.append('city', selectedCity.value);

      const queryString = `?${queryParams.toString()}`;

      let orgResults = { docs: [], total: 0 };
      let dealerResults = { docs: [], total: 0 };

      if (searchType === 'organizations') {
        try {
          const orgRes = await getPublicOrganizations(queryString);
          orgResults = orgRes?.data?.data || { docs: [], total: 0 };
        } catch (error) {
          console.log('Error fetching organizations:', error);
        }
      }

      if (searchType === 'dealers') {
        try {
          const dealerRes = await getPublicDealers(queryString);
          dealerResults = dealerRes?.data?.data || { docs: [], total: 0 };
        } catch (error) {
          console.log('Error fetching dealers:', error);
        }
      }

      const newOrgs = isLoadMore
        ? [...results.organizations, ...orgResults.docs]
        : orgResults.docs;
      const newDealers = isLoadMore
        ? [...results.dealers, ...dealerResults.docs]
        : dealerResults.docs;

      const totalResults = orgResults.total + dealerResults.total;
      const currentCount = newOrgs.length + newDealers.length;

      setResults({ organizations: newOrgs, dealers: newDealers });
      setTotal(totalResults);
      setPage(currentPage);
      setHasMore(currentCount < totalResults);
      setHasSearched(true);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Error fetching results. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.alternate.main,
          paddingTop: 6,
          paddingBottom: 2,
        }}
      >
        <Container>
          <SearchHero
            selectedState={selectedState}
            selectedCity={selectedCity}
            searchType={searchType}
            stateOptions={stateOptions}
            cityOptions={cityOptions}
            onStateChange={handleStateChange}
            onCityChange={handleCityChange}
            onTypeChange={handleTypeChange}
            onSearch={() => handleSearch(false)}
            loading={loading}
          />
        </Container>
      </Box>

      <Container>
        {loading && <ResultsGrid loading={true} />}

        {!loading && hasSearched && total === 0 && (
          <EmptyState onClearFilters={handleClearFilters} />
        )}

        {!loading && total > 0 && (
          <ResultsGrid
            organizations={results.organizations}
            dealers={results.dealers}
            total={total}
            hasMore={hasMore}
            onLoadMore={() => handleSearch(true)}
            loadingMore={loadingMore}
          />
        )}

        {!loading && !hasSearched && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            paddingY={8}
            textAlign="center"
          >
            <Box
              component="img"
              src="/images/drivolo-icon.png"
              alt="Drivolo"
              sx={{ width: 80, height: 80, opacity: 0.8, marginBottom: 2 }}
            />
            <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
              Select a state and city to find driving schools and car dealers near you
            </Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

export default SearchSection;
